import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { AccessTime, AccountBox, AccountTree, Chat, Feedback, Folder, LocationOn, People, Phone } from '@mui/icons-material';
import { Box, Button, Chip, Modal, Link as MuiLink, Skeleton, Stack, Typography } from '@mui/material';
import { getTextContentOnly, openInNewTab } from 'helpers/global.helper';
import useAuth from 'hooks/useAuth';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    useCreateMountingOrderByOrderIdMutation,
    useDeleteMountingJobMutation,
    useFetchCalendarInformationQuery,
    useFetchEventDurationQuery,
    useFetchJobsQuery,
    useFetchMountingOrderByOrderIdQuery,
    useLazyFetchEventDurationQuery,
    useSendMountingDeletionNotificationMutation,
    useSyncGoogleCalendarMutation
} from 'store/api/mounting.api';
import Swal from 'sweetalert2';
import { Client, Crew, Event, MountingOrderVariant, Project } from 'types/mounting.types';
import ModalFormCard from 'ui-component/cards/ModalCard';
import FileList from 'ui-component/files/FileList';
import { dateFormat } from 'utils/data/constant';
import EventEditForm from './EventEditForm';

interface Props {
    calendarId: string | undefined;
    event: Event;
    readOnly?: boolean;
}

const EventViewModal = NiceModal.create(({ calendarId, event, readOnly = false }: Props) => {
    const modal = useModal();
    const { user } = useAuth();
    const { t } = useTranslation();

    const [deleteMountingJob] = useDeleteMountingJobMutation();
    const [syncGoogleCalendar] = useSyncGoogleCalendarMutation();
    const [checkEventDuration] = useLazyFetchEventDurationQuery();
    const [sendMountingDeletionNotification] = useSendMountingDeletionNotificationMutation();
    const [createMountingOrder] = useCreateMountingOrderByOrderIdMutation();

    const { data: crews } = useFetchCalendarInformationQuery({ userId: user!.id, calendarId: calendarId });
    const { data: jobs } = useFetchJobsQuery(
        {
            region: crews?.map((item) => parseInt(item.id)),
            userID: user!.id
        },
        { skip: !crews }
    );
    const { data: duration } = useFetchEventDurationQuery({ event_id: event.Id }, { skip: !event });

    const [client, setClient] = useState<null | Client>();
    const [crew, setCrew] = useState<null | Crew>();
    const [project, setProject] = useState<null | Project>();

    const { data: mountingOrder } = useFetchMountingOrderByOrderIdQuery(
        { orderId: project?.id!, userId: user!.id },
        { skip: !user || !project }
    );

    useEffect(() => {
        if (!jobs || !crews) return;
        const client = jobs?.clients.find((obj) => obj.id === parseInt(event.client));
        if (client) setClient(client);
        const crew = crews?.find((obj) => obj.id === event.region);
        if (crew) setCrew(crew);
        const project = jobs?.projects.find((obj) => obj.id === event.project);
        if (project) setProject(project);
    }, [jobs, crews]);

    const handleDelete = () => {
        if (!user) return;
        modal.remove();
        checkEventDuration({ event_id: event.Id }).then(({ data }) => {
            if (!data) return;
            const events = data.events;
            sendMountingDeletionNotification({ orderId: parseInt(events[0].order_id), mountingId: events[0].local_id });
            toast.promise(
                deleteMountingJob({ local_id: events[0].local_id, selectedEvent: event.Id }).then(() => {
                    if (calendarId) syncGoogleCalendar({ id: user.id, region: calendarId });
                }),
                {
                    pending: t('notifications.pending.deletePending'),
                    error: t('notifications.error.deleteError'),
                    success: t('notifications.success.deleted')
                }
            );
        });
    };

    const handleCreateMountingOrder = async (variant: MountingOrderVariant) => {
        if (!project || !project.id) return;

        Swal.fire({
            title: t('pages.order_manage_page.mounting_order.do_you_want_to_create')
        }).then(({ isConfirmed }) => {
            if (isConfirmed)
                toast.promise(
                    createMountingOrder({ orderId: project.id, userId: user?.id!, variant }).then((result) => {
                        if ('data' in result && result.data.mounting_order_id) {
                            openInNewTab(`/mounting/order/${result.data.mounting_order_id}`);
                        }
                    }),
                    {
                        pending: t('notifications.pending.createPending'),
                        success: t('notifications.success.created')
                    }
                );
        });
    };

    return (
        <Modal open={modal.visible} onClose={() => modal.remove()}>
            <ModalFormCard
                title={event.Subject}
                sx={{
                    minwidth: 400
                }}
            >
                {/* Start and End Times */}
                <Stack gap={2} mb={2}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <AccessTime />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: 'calc(100%)'
                            }}
                        >
                            {moment(event.StartTime).format(`${dateFormat} HH:mm`)} - {moment(event.EndTime).format(`${dateFormat} HH:mm`)}
                        </Typography>
                    </Box>
                    {/* Selected project number */}
                    {project && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <AccountTree />
                            <Stack>
                                <Typography
                                    component={Link}
                                    to={`/order/${project.id}`}
                                    variant="h6"
                                    onClick={() => modal.remove()}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)',
                                        textDecoration: 'none',
                                        color: 'revert',
                                        fontWeight: 600
                                    }}
                                >
                                    {project.title}
                                </Typography>
                                {/* TODO */}
                                {event.variant === 'survey' ? (
                                    <>
                                        {mountingOrder?.survey ? (
                                            <Typography
                                                component={Link}
                                                to={`/mounting/order/${mountingOrder.survey.id}`}
                                                variant="h6"
                                                onClick={() => modal.remove()}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 'calc(100%)',
                                                    textDecoration: 'none',
                                                    color: 'revert',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {mountingOrder.survey.title}
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="h6"
                                                onClick={() => {
                                                    handleCreateMountingOrder('survey');
                                                }}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 'calc(100%)',
                                                    color: 'revert',
                                                    fontWeight: 600,
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('pages.mounting_orders_page.create_survey_mounting_order')}
                                            </Typography>
                                        )}
                                    </>
                                ) : event.variant === 'fitting' ? (
                                    <>
                                        {mountingOrder?.fitting ? (
                                            <Typography
                                                component={Link}
                                                to={`/mounting/order/${mountingOrder.fitting.id}`}
                                                variant="h6"
                                                onClick={() => modal.remove()}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 'calc(100%)',
                                                    textDecoration: 'none',
                                                    color: 'revert',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {mountingOrder.fitting.title}
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="h6"
                                                onClick={() => {
                                                    handleCreateMountingOrder('fitting');
                                                }}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 'calc(100%)',
                                                    color: 'revert',
                                                    fontWeight: 600,
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('pages.mounting_orders_page.create_fitting_mounting_order')}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <></>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Feedback if exists */}
                    {event.variant && event.feedback && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <Feedback />
                            <Typography
                                variant="h6"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: 'calc(100%)',
                                    textDecoration: 'none',
                                    color: 'revert',
                                    fontWeight: 600
                                }}
                            >
                                {getTextContentOnly(event.feedback)}
                            </Typography>
                        </Box>
                    )}
                    {/* Mounting Order */}
                    {/* {project && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <LocalShipping />
                            <Box
                                sx={{
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                {mountingOrder ? (
                                    <>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                whiteSpace: 'break-spaces',
                                                width: 'calc(100%)',
                                                mb: 0.5
                                            }}
                                        >
                                            {mountingOrder.title}
                                        </Typography>
                                        <Link to={`/mounting/order/${mountingOrder.id}`}>Visit</Link>
                                    </>
                                ) : (
                                    <>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                whiteSpace: 'break-spaces',
                                                width: 'calc(100%)',
                                                mb: 0.5
                                            }}
                                        >
                                            Mounting Order Doesn't exist
                                        </Typography>
                                        <Button onClick={handleCreateMountingOrder} variant="contained" size="small">
                                            Create
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )} */}
                    {/* Assigned Workers */}
                    {crew !== undefined && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <People />
                            {crew ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {crew && crew.crew_name && (
                                        <Chip label={crew.crew_name} key={crew.crew_name} size="small" variant="outlined" />
                                    )}
                                </Box>
                            ) : (
                                <Skeleton width={80} height={24} />
                            )}
                        </Box>
                    )}
                    {/* Selected Client */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center'
                        }}
                    >
                        <AccountBox />
                        {client ? (
                            <Typography
                                variant="h6"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: 'calc(100%)'
                                }}
                            >
                                {client.title}
                            </Typography>
                        ) : (
                            <Skeleton width={80} sx={{ fontSize: '0.75rem' }} />
                        )}
                    </Box>
                    {/* Job Address */}
                    {event.address && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <LocationOn />
                            <Box
                                sx={{
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'break-spaces',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {event.address}
                                </Typography>
                                {event.postalPlace && (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            whiteSpace: 'break-spaces',
                                            width: 'calc(100%)'
                                        }}
                                    >
                                        {event.postalPlace}
                                    </Typography>
                                )}

                                {event.postalCode && (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            whiteSpace: 'break-spaces',
                                            width: 'calc(100%)'
                                        }}
                                    >
                                        {event.postalCode}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                    {/* Phone Number */}
                    {event.phoneNumber && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <Phone />
                            <MuiLink href={`tel:${event.phoneNumber}`}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {event.phoneNumber}
                                </Typography>
                            </MuiLink>
                        </Box>
                    )}
                    {/* Job Comment */}
                    {event.comment && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            <Chat />
                            <Typography
                                variant="h6"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: 'calc(100%)'
                                }}
                            >
                                {event.comment}
                            </Typography>
                        </Box>
                    )}
                    {/* Files from client */}
                    {duration && duration.clientFiles && duration.clientFiles.length > 0 && (
                        <Stack gap={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <Folder />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {t('pages.mounting_scheduler_page.client_files')}
                                </Typography>
                            </Box>
                            <FileList files={duration.clientFiles} />
                        </Stack>
                    )}
                    {/* Factory files */}
                    {duration && duration.factoryFiles && duration.factoryFiles.length > 0 && (
                        <Stack gap={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <Folder />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {t('pages.mounting_scheduler_page.factory_files')}
                                </Typography>
                            </Box>
                            <FileList files={duration.factoryFiles} />
                        </Stack>
                    )}
                    {/* Designer files */}
                    {duration && duration.designerFiles && duration.designerFiles.length > 0 && (
                        <Stack gap={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <Folder />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {t('pages.mounting_scheduler_page.designer_files')}
                                </Typography>
                            </Box>
                            <FileList files={duration.designerFiles} />
                        </Stack>
                    )}
                    {/* Documentation files */}
                    {duration && duration.documentationFiles && duration.documentationFiles.length > 0 && (
                        <Stack gap={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <Folder />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)'
                                    }}
                                >
                                    {t('pages.mounting_scheduler_page.documentation_files')}
                                </Typography>
                            </Box>
                            <FileList files={duration.documentationFiles} />
                        </Stack>
                    )}
                </Stack>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button color="primary" size="small" variant="contained" onClick={() => modal.remove()}>
                        {t('global.buttons.save')}
                    </Button>
                    {!readOnly && (
                        <>
                            <Button
                                color="secondary"
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    modal.remove();
                                    if (!duration) return;
                                    NiceModal.show(EventEditForm, {
                                        event: {
                                            ...event,
                                            designerFiles: duration.designerFiles,
                                            documentationFiles: duration.documentationFiles,
                                            factoryFiles: duration.factoryFiles,
                                            clientFiles: duration.clientFiles
                                        },
                                        calendarId
                                    });
                                }}
                            >
                                {t('global.buttons.edit')}
                            </Button>
                            <Button color="error" size="small" variant="contained" onClick={() => handleDelete()}>
                                {t('global.buttons.delete')}
                            </Button>
                        </>
                    )}
                </Box>
            </ModalFormCard>
        </Modal>
    );
});

export default EventViewModal;
